import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import {
    updateNoteReceivable,
    getNoteReceivableDetails,
    getCustomers,
    clearError,
    clearSuccess,
    addCustomer,
    getProjects,
    addProject,
    getCostCenters,
    addCostCenter,
    getTaxes,
    addTax
} from '../../../../redux/Slices/authSlice';
import i18n from '../../../../i18n/i18n';

// Edit Note Receivable Component
const EditNoteReceivable = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const {
        customers,
        customersPagination,
        projects,
        projectsPagination,
        costCenters,
        costCentersPagination,
        taxes,
        taxesPagination,
        isLoading,
        error,
        success
    } = useSelector((s) => s.auth);

    const [form, setForm] = useState({
        customer_id: '',
        project_id: '',
        amount: '',
        issue_date: '',
        due_date: '',
        note_number: '',
        description: '',
    });

    const [selectedCustomerMeta, setSelectedCustomerMeta] = useState(null);
    const [selectedProjectMeta, setSelectedProjectMeta] = useState(null);

    const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
    const customerSearchInputRef = useRef(null);
    const customerSearchTimeoutRef = useRef(null);

    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
    const [projectSearchTerm, setProjectSearchTerm] = useState('');
    const [projectCurrentPage, setProjectCurrentPage] = useState(1);
    const projectSearchInputRef = useRef(null);
    const projectSearchTimeoutRef = useRef(null);

    const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
    const [taxSearchTerm, setTaxSearchTerm] = useState('');
    const [taxCurrentPage, setTaxCurrentPage] = useState(1);
    const taxSearchInputRef = useRef(null);
    const taxSearchTimeoutRef = useRef(null);

    const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
    const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
    const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
    const costCenterSearchInputRef = useRef(null);
    const costCenterSearchTimeoutRef = useRef(null);

    const [projModalCustomerDropdownOpen, setProjModalCustomerDropdownOpen] = useState(false);
    const [projModalCustomerSearchTerm, setProjModalCustomerSearchTerm] = useState('');
    const [projModalCustomerCurrentPage, setProjModalCustomerCurrentPage] = useState(1);
    const projModalCustomerSearchInputRef = useRef(null);
    const projModalCustomerSearchTimeoutRef = useRef(null);

    const lastErrorRef = useRef({ message: null, time: 0 });

    // Modals state
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [showAddProjectModal, setShowAddProjectModal] = useState(false);
    const [showAddTaxModal, setShowAddTaxModal] = useState(false);
    const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);

    const [customerForm, setCustomerForm] = useState({
        name_en: '',
        name_ar: '',
        code: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        tax_number: '',
        is_active: true,
        notes: ''
    });
    const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);

    const [projectForm, setProjectForm] = useState({
        name_ar: '',
        name_en: '',
        code: '',
        customer_id: '',
        start_date: '',
        end_date: '',
        status: 'planned',
        contract_value: '',
        tax_id: '',
        cost_center_id: '',
        notes: ''
    });
    const [isSubmittingProject, setIsSubmittingProject] = useState(false);

    const [taxForm, setTaxForm] = useState({
        name_ar: '',
        name_en: '',
        rate: ''
    });

    const [costCenterForm, setCostCenterForm] = useState({
        name_ar: '',
        name_en: '',
        code: ''
    });

    // Load customers when dropdown opens or pagination/search
    useEffect(() => {
        if (customerDropdownOpen) {
            dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined }));
        }
    }, [dispatch, customerCurrentPage, customerDropdownOpen, customerSearchTerm]);

    // Load projects
    useEffect(() => {
        if (projectDropdownOpen) {
            dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
        }
    }, [dispatch, projectCurrentPage, projectDropdownOpen, projectSearchTerm]);

    // Load taxes
    useEffect(() => {
        if (taxDropdownOpen) {
            dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
        }
    }, [dispatch, taxCurrentPage, taxDropdownOpen, taxSearchTerm]);

    // Load cost centers
    useEffect(() => {
        if (costCenterDropdownOpen) {
            dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
        }
    }, [dispatch, costCenterCurrentPage, costCenterDropdownOpen, costCenterSearchTerm]);

    // Load customers for project modal
    useEffect(() => {
        if (projModalCustomerDropdownOpen) {
            dispatch(getCustomers({ page: projModalCustomerCurrentPage, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
        }
    }, [dispatch, projModalCustomerCurrentPage, projModalCustomerDropdownOpen, projModalCustomerSearchTerm]);

    const normalizeNoteDetails = (payload) => {
        const candidates = [payload?.data?.data, payload?.data, payload];
        for (const cand of candidates) {
            if (cand && typeof cand === 'object') {
                if ('customer_id' in cand || 'customer' in cand || 'note_number' in cand || 'issue_date' in cand) {
                    return cand;
                }
                if (cand?.data && typeof cand.data === 'object') {
                    return cand.data;
                }
            }
        }
        return payload;
    };

    // Load note receivable details on mount
    useEffect(() => {
        if (id) {
            dispatch(getNoteReceivableDetails(id))
                .unwrap()
                .then((response) => {
                    const data = normalizeNoteDetails(response);
                    const resolvedCustomerId = data.customer_id ?? data.customer?.id ?? '';
                    const resolvedProjectId = data.project_id ?? data.project?.id ?? '';
                    setForm({
                        customer_id: resolvedCustomerId ? String(resolvedCustomerId) : '',
                        project_id: resolvedProjectId ? String(resolvedProjectId) : '',
                        amount: data.amount || '',
                        issue_date: data.issue_date ? String(data.issue_date).split('T')[0] : '',
                        due_date: data.due_date ? String(data.due_date).split('T')[0] : '',
                        note_number: data.note_number || '',
                        description: data.description || ''
                    });

                    if (data.customer) {
                        setSelectedCustomerMeta({
                            id: String(data.customer.id),
                            code: data.customer.code,
                            name_ar: data.customer.name_ar,
                            name_en: data.customer.name_en,
                        });
                    }
                    if (data.project) {
                        setSelectedProjectMeta({
                            id: String(data.project.id),
                            code: data.project.code,
                            name_ar: data.project.name_ar,
                            name_en: data.project.name_en,
                        });
                    }
                })
                .catch(() => {
                    toast.error(t('notesReceivable333.form.loadError', { defaultValue: 'فشل تحميل تفاصيل ورقة القبض' }), { rtl: isRTL });
                });
        }
    }, [id, dispatch, t, isRTL]);

    // Search debounces
    useEffect(() => {
        if (customerSearchTerm !== undefined && customerDropdownOpen) {
            if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
            customerSearchTimeoutRef.current = setTimeout(() => {
                setCustomerCurrentPage(1);
                dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined }));
            }, 300);
        }
        return () => { if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current); };
    }, [customerSearchTerm, customerDropdownOpen, dispatch]);

    useEffect(() => {
        if (projectSearchTerm !== undefined && projectDropdownOpen) {
            if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
            projectSearchTimeoutRef.current = setTimeout(() => {
                setProjectCurrentPage(1);
                dispatch(getProjects({ page: 1, per_page: 10, search: projectSearchTerm || undefined }));
            }, 300);
        }
        return () => { if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current); };
    }, [projectSearchTerm, projectDropdownOpen, dispatch]);

    useEffect(() => {
        if (taxSearchTerm !== undefined && taxDropdownOpen) {
            if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
            taxSearchTimeoutRef.current = setTimeout(() => {
                setTaxCurrentPage(1);
                dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined }));
            }, 300);
        }
        return () => { if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current); };
    }, [taxSearchTerm, taxDropdownOpen, dispatch]);

    useEffect(() => {
        if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
            if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
            costCenterSearchTimeoutRef.current = setTimeout(() => {
                setCostCenterCurrentPage(1);
                dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined }));
            }, 300);
        }
        return () => { if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current); };
    }, [costCenterSearchTerm, costCenterDropdownOpen, dispatch]);

    useEffect(() => {
        if (projModalCustomerSearchTerm !== undefined && projModalCustomerDropdownOpen) {
            if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current);
            projModalCustomerSearchTimeoutRef.current = setTimeout(() => {
                setProjModalCustomerCurrentPage(1);
                dispatch(getCustomers({ page: 1, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
            }, 300);
        }
        return () => { if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current); };
    }, [projModalCustomerSearchTerm, projModalCustomerDropdownOpen, dispatch]);

    // Success handling
    useEffect(() => {
        if (success) {
            // Prevent navigation if any modal is open
            const anyModalOpen = showAddCustomerModal || showAddProjectModal || showAddTaxModal || showAddCostCenterModal;

            if (!anyModalOpen) {
                toast.success(success, { rtl: isRTL });
                dispatch(clearSuccess());
                navigate('/notes-receivable');
            } else {
                // If modal is open, we let the local handler deal with success if needed
                // or just clear it to avoid global toast
                dispatch(clearSuccess());
            }
        }
    }, [success, dispatch, navigate, showAddCustomerModal, showAddProjectModal, showAddTaxModal, showAddCostCenterModal, isRTL]);

    // Error handling
    useEffect(() => {
        if (error) {
            // Prevent showing global toasts if any modal is open
            const anyModalOpen = showAddCustomerModal || showAddProjectModal || showAddTaxModal || showAddCostCenterModal;

            if (!anyModalOpen) {
                const now = Date.now();
                const last = lastErrorRef.current;
                if (!last.message || last.message !== error || now - last.time > 2000) {
                    toast.error(error, { rtl: isRTL });
                    lastErrorRef.current = { message: error, time: now };
                }
            }
            // Clear error after some time
            const timer = setTimeout(() => dispatch(clearError()), 2500);
            return () => clearTimeout(timer);
        }
    }, [error, dispatch, showAddCustomerModal, showAddProjectModal, showAddTaxModal, showAddCostCenterModal, isRTL]);

    const displayCustomers = Array.isArray(customers) ? customers : [];
    const customerPagination = customersPagination || { current_page: 1, last_page: 1 };
    const displayProjects = Array.isArray(projects) ? projects : [];
    const projectPagination = projectsPagination || { current_page: 1, last_page: 1 };
    const displayTaxes = Array.isArray(taxes) ? taxes : [];
    const taxPagination = taxesPagination || { current_page: 1, last_page: 1 };
    const displayCostCenters = Array.isArray(costCenters) ? costCenters : [];
    const costCenterPagination = costCentersPagination || { current_page: 1, last_page: 1 };

    const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
        if (!item) return '';
        const code = item[codeField] || '';
        const name = isRTL
            ? (item[nameArField] || item[nameEnField] || '')
            : (item[nameEnField] || item[nameArField] || '');
        return `${code ? `${code} - ` : ''}${name}`;
    };

    const renderCustomerDisplay = (customerId) => {
        if (!customerId) return t('notesReceivable333.form.selectCustomer', { defaultValue: 'اختر العميل' });
        const selected = (customers || []).find((c) => String(c.id) === String(customerId));
        if (selected) return renderDropdownItemLabel(selected, 'code', 'name_ar', 'name_en');
        if (selectedCustomerMeta && String(selectedCustomerMeta.id) === String(customerId)) {
            return renderDropdownItemLabel(selectedCustomerMeta, 'code', 'name_ar', 'name_en');
        }
        return t('notesReceivable333.form.selectCustomer', { defaultValue: 'اختر العميل' });
    };

    const renderProjectDisplay = (projectId) => {
        if (!projectId) return t('notesReceivable333.form.selectProject', { defaultValue: 'اختر المشروع' });
        const selected = (projects || []).find((p) => String(p.id) === String(projectId));
        if (selected) return renderDropdownItemLabel(selected, 'code', 'name_ar', 'name_en');
        if (selectedProjectMeta && String(selectedProjectMeta.id) === String(projectId)) {
            return renderDropdownItemLabel(selectedProjectMeta, 'code', 'name_ar', 'name_en');
        }
        return t('notesReceivable333.form.selectProject', { defaultValue: 'اختر المشروع' });
    };

    const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
        const internalInputRef = useRef(null);
        const inputRef = searchInputRef || internalInputRef;

        useEffect(() => {
            if (isOpen && inputRef?.current) {
                const timer = setTimeout(() => {
                    inputRef.current?.focus({ preventScroll: true });
                }, 0);
                return () => clearTimeout(timer);
            }
        }, [isOpen, searchTerm, items, inputRef]);

        if (!isOpen) return null;

        return (
            <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
                <div style={{ padding: 8 }}>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
                        value={searchTerm}
                        onChange={(e) => {
                            onSearchChange(e.target.value);
                            setTimeout(() => {
                                inputRef.current?.focus({ preventScroll: true });
                            }, 0);
                        }}
                        onFocus={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
                    />
                </div>
                {pagination && pagination.last_page >= 1 && (
                    <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
                            {(() => {
                                const pages = [];
                                const current = pagination.current_page;
                                const last = pagination.last_page;
                                const showPages = 5;
                                let start = Math.max(1, current - Math.floor(showPages / 2));
                                let end = Math.min(last, start + showPages - 1);
                                if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
                                for (let i = start; i <= end; i++) pages.push(i);
                                return pages.map((page) => (
                                    <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
                                ));
                            })()}
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
                        </div>
                    </div>
                )}
                <div>
                    {(Array.isArray(items) ? items : []).map((item) => (
                        <div
                            key={item.id}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onSelect(item.id);
                            }}
                            style={{
                                padding: '12px 15px',
                                cursor: 'pointer',
                                borderTop: '1px solid #333',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                color: 'white',
                                fontSize: '14px',
                                transition: 'background-color 0.2s',
                                direction: isRTL ? 'rtl' : 'ltr',
                                textAlign: isRTL ? 'right' : 'left'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14263b'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <span style={{ color: 'white' }}>
                                {renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}
                            </span>
                        </div>
                    ))}
                    {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
                </div>
            </div>
        );
    };

    const handleTaxFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTaxForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCostCenterFormChange = (e) => {
        const { name, value } = e.target;
        setCostCenterForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCustomerFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCustomerForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };
    const handleProjectFormChange = (e) => {
        const { name, value } = e.target;
        setProjectForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddCustomer = async (e) => {
        e.preventDefault();
        if (!customerForm.name_ar || !customerForm.name_en) {
            toast.error(t('quotations.add.modals.addCustomer.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية والإنجليزية' }), { rtl: isRTL });
            return;
        }
        try {
            setIsSubmittingCustomer(true);
            const submitData = {
                ...customerForm,
                is_active: customerForm.is_active ? true : false
            };
            const result = await dispatch(addCustomer(submitData)).unwrap();
            if (result?.data?.id) {
                dispatch(clearSuccess());
                if (showAddProjectModal) {
                    setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
                } else {
                    setForm(prev => ({ ...prev, customer_id: result.data.id }));
                }
                await dispatch(getCustomers({ page: 1, per_page: 10 }));
                setShowAddCustomerModal(false);
                setCustomerForm({
                    name_en: '',
                    name_ar: '',
                    code: '',
                    contact_person: '',
                    email: '',
                    phone: '',
                    address: '',
                    tax_number: '',
                    is_active: true,
                    notes: ''
                });
                toast.success(t('quotations.add.modals.addCustomer.success', { defaultValue: 'تم إضافة العميل بنجاح' }), { rtl: isRTL });
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || t('quotations.add.modals.addCustomer.error', { defaultValue: 'فشل إضافة العميل' }), { rtl: isRTL });
        } finally {
            setIsSubmittingCustomer(false);
        }
    };

    const handleAddProject = async (e) => {
        e.preventDefault();
        if (!projectForm.name_ar) {
            toast.error(t('quotations.add.modals.addProject.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية' }), { rtl: isRTL });
            return;
        }
        try {
            setIsSubmittingProject(true);
            const payload = {
                ...projectForm,
                contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
                customer_id: projectForm.customer_id || null,
                tax_id: projectForm.tax_id || null,
                cost_center_id: projectForm.cost_center_id || null,
            };
            const result = await dispatch(addProject(payload)).unwrap();
            if (result?.data?.id) {
                dispatch(clearSuccess());
                setForm(prev => ({ ...prev, project_id: result.data.id }));
                await dispatch(getProjects({ page: 1, per_page: 10 }));
                setShowAddProjectModal(false);
                setProjectForm({
                    code: '',
                    name_ar: '',
                    name_en: '',
                    customer_id: '',
                    start_date: '',
                    end_date: '',
                    status: 'planned',
                    contract_value: '',
                    tax_id: '',
                    cost_center_id: '',
                    notes: '',
                });
                toast.success(t('quotations.add.modals.addProject.success', { defaultValue: 'تم إضافة المشروع بنجاح' }), { rtl: isRTL });
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || t('quotations.add.modals.addProject.error', { defaultValue: 'فشل إضافة المشروع' }), { rtl: isRTL });
        } finally {
            setIsSubmittingProject(false);
        }
    };

    const handleAddTax = async (e) => {
        e.preventDefault();
        if (!taxForm.name_ar || !taxForm.rate) {
            toast.error(t('quotations.add.modals.addTax.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
            return;
        }
        try {
            const payload = { ...taxForm, rate: Number(taxForm.rate) };
            const result = await dispatch(addTax(payload)).unwrap();
            if (result?.data?.id) {
                dispatch(clearSuccess());
                setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
                await dispatch(getTaxes({ page: 1, per_page: 10 }));
                setShowAddTaxModal(false);
                setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
                toast.success(t('quotations.add.modals.addTax.success', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
            }
        } catch (error) {
            toast.error(error?.message || t('quotations.add.modals.addTax.error', { defaultValue: 'فشل إضافة الضريبة' }), { rtl: isRTL });
        }
    };

    const handleAddCostCenter = async (e) => {
        e.preventDefault();
        if (!costCenterForm.name_ar) {
            toast.error(t('quotations.add.modals.addCostCenter.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
            return;
        }
        try {
            const result = await dispatch(addCostCenter(costCenterForm)).unwrap();
            if (result?.data?.id) {
                dispatch(clearSuccess());
                setProjectForm(prev => ({ ...prev, cost_center_id: result.data.id }));
                await dispatch(getCostCenters({ page: 1, per_page: 10 }));
                setShowAddCostCenterModal(false);
                setCostCenterForm({ name_ar: '', name_en: '', code: '' });
                toast.success(t('quotations.add.modals.addCostCenter.success', { defaultValue: 'تم إضافة مركز التكلفة بنجاح' }), { rtl: isRTL });
            }
        } catch (error) {
            toast.error(error?.message || t('quotations.add.modals.addCostCenter.error', { defaultValue: 'فشل إضافة مركز التكلفة' }), { rtl: isRTL });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        if (!form.customer_id) {
            toast.error(t('notesReceivable333.form.customerRequired', { defaultValue: 'برجاء اختيار العميل' }), { rtl: isRTL });
            return;
        }
        if (!form.amount || form.amount === '' || form.amount === '0') {
            toast.error(t('notesReceivable333.form.amountRequired', { defaultValue: 'القيمة مطلوبة' }), { rtl: isRTL });
            return;
        }
        setIsSubmitting(true);
        const payload = {
            customer_id: Number(form.customer_id),
            amount: Number(form.amount),
        };

        if (form.project_id) {
            payload.project_id = Number(form.project_id);
        }

        // Only include optional fields if they have values
        if (form.issue_date) {
            payload.issue_date = form.issue_date;
        }
        if (form.due_date) {
            payload.due_date = form.due_date;
        }
        if (form.note_number) {
            payload.note_number = form.note_number;
        }
        if (form.description) {
            payload.description = form.description;
        }

        try {
            await dispatch(updateNoteReceivable({ id, payload })).unwrap();
            toast.success(t('notesReceivable333.edit.success', { defaultValue: 'تم التعديل بنجاح' }), { rtl: isRTL });
            navigate('/notes-receivable');
        } catch (err) {
            // Error toast is handled by the global error useEffect in this component
        } finally {
            setIsSubmitting(false);
        }
    };

    // Close dropdowns when clicking outside
    // Close all dropdowns when clicking outside (main form and add project modal)
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            const isDropdownContainer = target.closest('[data-dropdown-container]');
            const isDropdownToggle = target.closest('[data-dropdown-toggle]');
            if (!isDropdownContainer && !isDropdownToggle) {
                setCustomerDropdownOpen(false);
                setProjectDropdownOpen(false);
                setTaxDropdownOpen(false);
                setCostCenterDropdownOpen(false);
                setProjModalCustomerDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [customerDropdownOpen, projectDropdownOpen, taxDropdownOpen, costCenterDropdownOpen, projModalCustomerDropdownOpen]);

    // Helper to ensure only one dropdown is open at a time in add project modal
    const handleProjModalDropdownToggle = (dropdownSetter, others) => {
        dropdownSetter((open) => {
            if (!open) {
                others.forEach((fn) => fn(false));
            }
            return !open;
        });
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
            <h1 style={{ marginTop: 0, marginBottom: 16, fontSize: '20px', textAlign: isRTL ? 'right' : 'left' }}>{t('notesReceivable333.edit.title')}</h1>
            <form
                onSubmit={handleSubmit}
                style={{
                    backgroundColor: '#1b3350',
                    border: '1px solid #333',
                    borderRadius: 12,
                    padding: 24,
                    display: 'grid',
                    gap: 16,
                }}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                    <div style={{ position: 'relative' }} data-dropdown-container>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <label style={{ display: 'block', fontSize: 14, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                                {t('notesReceivable333.form.customer', { defaultValue: 'العميل' })} <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAddCustomerModal(true);
                                }}
                                style={{
                                    backgroundColor: '#AC2000',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 12,
                                }}
                                title="إضافة عميل جديد"
                            >
                                <FaPlus /> {t('notesReceivable333.form.add', { defaultValue: 'إضافة' })}
                            </button>
                        </div>
                        <div
                            data-dropdown-toggle
                            onClick={() => {
                                if (!customerDropdownOpen) {
                                    setCustomerSearchTerm('');
                                    setCustomerCurrentPage(1);
                                    dispatch(getCustomers({ page: 1, per_page: 10 }));
                                }
                                setCustomerDropdownOpen((p) => !p);
                                setProjectDropdownOpen(false);
                            }}
                            style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                            <span>{renderCustomerDisplay(form.customer_id)}</span>
                            <span style={{ fontSize: 10 }}>▼</span>
                        </div>
                        <DropdownWithSearch
                            isOpen={customerDropdownOpen}
                            onToggle={() => setCustomerDropdownOpen(!customerDropdownOpen)}
                            searchTerm={customerSearchTerm}
                            onSearchChange={setCustomerSearchTerm}
                            searchInputRef={customerSearchInputRef}
                            items={displayCustomers}
                            pagination={customerPagination}
                            currentPage={customerCurrentPage}
                            onPageChange={(page) => {
                                setCustomerCurrentPage(page);
                                dispatch(getCustomers({ page, per_page: 10, search: customerSearchTerm || undefined }));
                            }}
                            onSelect={(val) => {
                                setForm((prev) => ({ ...prev, customer_id: val }));
                                setCustomerDropdownOpen(false);
                            }}
                            label={t('notesReceivable333.form.customer', { defaultValue: 'العميل' })}
                        />
                    </div>

                    <div style={{ position: 'relative' }} data-dropdown-container>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <label style={{ display: 'block', fontSize: 14, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                                {t('notesReceivable333.form.project', { defaultValue: 'المشروع' })}
                            </label>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAddProjectModal(true);
                                }}
                                style={{
                                    backgroundColor: '#AC2000',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 12,
                                }}
                                title="إضافة مشروع جديد"
                            >
                                <FaPlus /> {t('notesReceivable333.form.add', { defaultValue: 'إضافة' })}
                            </button>
                        </div>
                        <div
                            data-dropdown-toggle
                            onClick={() => {
                                if (!projectDropdownOpen) {
                                    setProjectSearchTerm('');
                                    setProjectCurrentPage(1);
                                    dispatch(getProjects({ page: 1, per_page: 10 }));
                                }
                                setProjectDropdownOpen((p) => !p);
                                setCustomerDropdownOpen(false);
                            }}
                            style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                            <span>{renderProjectDisplay(form.project_id)}</span>
                            <span style={{ fontSize: 10 }}>▼</span>
                        </div>
                        <DropdownWithSearch
                            isOpen={projectDropdownOpen}
                            onToggle={() => setProjectDropdownOpen(!projectDropdownOpen)}
                            searchTerm={projectSearchTerm}
                            onSearchChange={setProjectSearchTerm}
                            searchInputRef={projectSearchInputRef}
                            items={displayProjects}
                            pagination={projectPagination}
                            currentPage={projectCurrentPage}
                            onPageChange={(page) => {
                                setProjectCurrentPage(page);
                                dispatch(getProjects({ page, per_page: 10, search: projectSearchTerm || undefined }));
                            }}
                            onSelect={(val) => {
                                setForm((prev) => ({ ...prev, project_id: val }));
                                setProjectDropdownOpen(false);
                            }}
                            label={t('notesReceivable333.form.project', { defaultValue: 'المشروع' })}
                        />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <label style={{ display: 'block', fontSize: 14, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                                {t('notesReceivable333.form.amount', { defaultValue: 'القيمة' })} <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <div style={{ padding: '6px 10px', visibility: 'hidden', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                <FaPlus /> {t('notesReceivable333.form.add', { defaultValue: 'إضافة' })}
                            </div>
                        </div>
                        <input
                            type="number"

                            name="amount"
                            value={form.amount}
                            onChange={handleChange}
                            required
                            style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, boxSizing: 'border-box' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>
                            {t('notesReceivable333.form.issueDate', { defaultValue: 'تاريخ الإصدار' })}
                        </label>
                        <input
                            type="date"
                            name="issue_date"
                            value={form.issue_date}
                            onChange={handleChange}
                            style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, boxSizing: 'border-box' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>
                            {t('notesReceivable333.form.dueDate', { defaultValue: 'تاريخ الاستحقاق' })}
                        </label>
                        <input
                            type="date"
                            name="due_date"
                            value={form.due_date}
                            onChange={handleChange}
                            style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, boxSizing: 'border-box' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>
                            {t('notesReceivable333.form.noteNumber', { defaultValue: 'رقم السند/الشيك' })}
                        </label>
                        <input
                            type="text"
                            name="note_number"
                            value={form.note_number}
                            onChange={handleChange}
                            style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, boxSizing: 'border-box' }}
                        />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign: isRTL ? 'right' : 'left' }}>
                            {t('notesReceivable333.form.description', { defaultValue: 'البيان' })}
                        </label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            rows={3}
                            style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                    <button
                        type="button"
                        onClick={() => navigate('/notes-receivable')}
                        style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}
                    >
                        {t('notesReceivable333.form.cancel', { defaultValue: 'إلغاء' })}
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.7 : 1 }}
                    >
                        {isSubmitting ? '...' : t('notesReceivable333.form.save', { defaultValue: 'حفظ' })}
                    </button>
                </div>
            </form >

            {/* Add Customer Modal */}
            {
                showAddCustomerModal && createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            zIndex: 999999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px'
                        }}
                        onClick={() => setShowAddCustomerModal(false)}
                    >
                        <div
                            style={{
                                backgroundColor: '#1b3350',
                                borderRadius: '12px',
                                padding: '30px',
                                maxWidth: '900px',
                                width: '100%',
                                maxHeight: '90vh',
                                overflowY: 'auto',
                                border: '1px solid #333',
                                direction: isRTL ? 'rtl' : 'ltr'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.title', { defaultValue: 'إضافة عميل جديد' })}</h2>
                                <button
                                    onClick={() => {
                                        setShowAddCustomerModal(false);
                                        setCustomerForm({
                                            name_en: '',
                                            name_ar: '',
                                            code: '',
                                            contact_person: '',
                                            email: '',
                                            phone: '',
                                            address: '',
                                            tax_number: '',
                                            is_active: true,
                                            notes: ''
                                        });
                                    }}
                                    style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: 'white',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        padding: '0',
                                        width: '30px',
                                        height: '30px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                            <form onSubmit={handleAddCustomer}>
                                {/* Form fields reused from AddNoteReceivable */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('quotations.add.modals.addCustomer.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <input type="text" name="name_en" value={customerForm.name_en} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('quotations.add.modals.addCustomer.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <input type="text" name="name_ar" value={customerForm.name_ar} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('quotations.add.modals.addCustomer.code', { defaultValue: 'الكود' })}
                                        </label>
                                        <input type="text" name="code" value={customerForm.code} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('quotations.add.modals.addCustomer.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                                        </label>
                                        <input type="text" name="contact_person" value={customerForm.contact_person} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    {/* Email, Phone, Address, Tax Number, Is Active, Notes - Adding simplified similar blocks */}
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.email', { defaultValue: 'البريد الإلكتروني' })}</label>
                                        <input type="email" name="email" value={customerForm.email} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.phone', { defaultValue: 'الهاتف' })}</label>
                                        <input type="text" name="phone" value={customerForm.phone} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.address', { defaultValue: 'العنوان' })}</label>
                                        <input type="text" name="address" value={customerForm.address} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.taxNumber', { defaultValue: 'الرقم الضريبي' })}</label>
                                        <input type="text" name="tax_number" value={customerForm.tax_number} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                        <input type="checkbox" name="is_active" checked={customerForm.is_active} onChange={handleCustomerFormChange} style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
                                        <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.isActive', { defaultValue: 'نشط' })}</label>
                                    </div>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.notes', { defaultValue: 'ملاحظات' })}</label>
                                        <textarea name="notes" value={customerForm.notes} onChange={handleCustomerFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} rows={4} style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAddCustomerModal(false);
                                            setCustomerForm({ name_en: '', name_ar: '', code: '', contact_person: '', email: '', phone: '', address: '', tax_number: '', is_active: true, notes: '' });
                                        }}
                                        style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        {t('quotations.add.modals.addCustomer.cancel', { defaultValue: 'إلغاء' })}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmittingCustomer}
                                        style={{ backgroundColor: isSubmittingCustomer ? '#666' : '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer', opacity: isSubmittingCustomer ? 0.7 : 1 }}
                                    >
                                        {isSubmittingCustomer ? t('quotations.add.modals.addCustomer.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addCustomer.save', { defaultValue: 'حفظ' })}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>,
                    document.body
                )
            }


            {showAddProjectModal && createPortal(
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowAddProjectModal(false)}>
                    <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', maxWidth: '800px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #333', direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}>{t('quotations.add.modals.addProject.title')}</h2>
                            <button onClick={() => setShowAddProjectModal(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleAddProject}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                                <div><label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.nameAr')} *</label><input type="text" name="name_ar" value={projectForm.name_ar} onChange={handleProjectFormChange} required style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} /></div>
                                <div><label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.nameEn')}</label><input type="text" name="name_en" value={projectForm.name_en} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} /></div>
                                <div><label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.code')}</label><input type="text" name="code" value={projectForm.code} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} /></div>

                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <label style={{ flex: 1, color: 'white' }}>{t('quotations.add.modals.addProject.customer')} *</label>
                                        <button type="button" onClick={() => setShowAddCustomerModal(true)} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}><FaPlus /></button>
                                    </div>
                                    <div data-dropdown-toggle onClick={() => {
                                        setProjModalCustomerDropdownOpen((open) => {
                                            if (!open) {
                                                setTaxDropdownOpen(false);
                                                setCostCenterDropdownOpen(false);
                                            }
                                            return !open;
                                        });
                                    }} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', cursor: 'pointer' }}>{renderCustomerDisplay(projectForm.customer_id)}</div>
                                    <DropdownWithSearch isOpen={projModalCustomerDropdownOpen} items={customers} onSelect={(id) => { setProjectForm(p => ({ ...p, customer_id: id })); setProjModalCustomerDropdownOpen(false); }} searchTerm={projModalCustomerSearchTerm} onSearchChange={setProjModalCustomerSearchTerm} pagination={customersPagination} onPageChange={(p) => setProjModalCustomerCurrentPage(p)} label="العميل" />
                                </div>

                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <label style={{ flex: 1, color: 'white' }}>{t('quotations.add.modals.addProject.tax')}</label>
                                        <button type="button" onClick={() => setShowAddTaxModal(true)} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}><FaPlus /></button>
                                    </div>
                                    <div data-dropdown-toggle onClick={() => {
                                        setTaxDropdownOpen((open) => {
                                            if (!open) {
                                                setProjModalCustomerDropdownOpen(false);
                                                setCostCenterDropdownOpen(false);
                                            }
                                            return !open;
                                        });
                                    }} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', cursor: 'pointer' }}>{renderDropdownItemLabel(taxes?.find(t => String(t.id) === String(projectForm.tax_id)), 'code', 'name_ar', 'name_en') || "اختر الضريبة"}</div>
                                    <DropdownWithSearch isOpen={taxDropdownOpen} items={taxes} onSelect={(id) => { setProjectForm(p => ({ ...p, tax_id: id })); setTaxDropdownOpen(false); }} searchTerm={taxSearchTerm} onSearchChange={setTaxSearchTerm} pagination={taxesPagination} onPageChange={(p) => setTaxCurrentPage(p)} label="الضريبة" />
                                </div>

                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <label style={{ flex: 1, color: 'white' }}>{t('quotations.add.modals.addProject.costCenter')}</label>
                                        <button type="button" onClick={() => setShowAddCostCenterModal(true)} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}><FaPlus /></button>
                                    </div>
                                    <div data-dropdown-toggle onClick={() => {
                                        setCostCenterDropdownOpen((open) => {
                                            if (!open) {
                                                setProjModalCustomerDropdownOpen(false);
                                                setTaxDropdownOpen(false);
                                            }
                                            return !open;
                                        });
                                    }} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', cursor: 'pointer' }}>{renderDropdownItemLabel(costCenters?.find(cc => String(cc.id) === String(projectForm.cost_center_id)), 'code', 'name_ar', 'name_en') || "اختر مركز التكلفة"}</div>
                                    <DropdownWithSearch isOpen={costCenterDropdownOpen} items={costCenters} onSelect={(id) => { setProjectForm(p => ({ ...p, cost_center_id: id })); setCostCenterDropdownOpen(false); }} searchTerm={costCenterSearchTerm} onSearchChange={setCostCenterSearchTerm} pagination={costCentersPagination} onPageChange={(p) => setCostCenterCurrentPage(p)} label="مركز التكلفة" />
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                                    <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                                    <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                                    <select name="status" value={projectForm.status} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }}>
                                        <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                                        <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                                        <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                                        <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                                        <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                                    <input type="number" name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                                </div>

                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'white' }}>{t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}</label>
                                    <textarea name="notes" value={projectForm.notes} onChange={handleProjectFormChange} rows={3} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', resize: 'vertical' }} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                                <button type="button" onClick={() => setShowAddProjectModal(false)} style={{ padding: '10px 20px', backgroundColor: '#6c757d', border: 'none', color: 'white', borderRadius: '6px', cursor: 'pointer' }}>{t('common.cancel')}</button>
                                <button type="submit" disabled={isSubmittingProject} style={{ padding: '10px 20px', backgroundColor: '#AC2000', border: 'none', color: 'white', borderRadius: '6px', cursor: isSubmittingProject ? 'not-allowed' : 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}>{isSubmittingProject ? '...' : t('common.save')}</button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {showAddTaxModal && createPortal(
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowAddTaxModal(false)}>
                    <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '100%', border: '1px solid #333', direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h2 style={{ color: 'white' }}>{t('quotations.add.modals.addTax.title')}</h2>
                            <button onClick={() => setShowAddTaxModal(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleAddTax}>
                            <div><label style={{ display: 'block', marginBottom: 8, color: 'white' }}>{t('quotations.add.modals.addTax.nameAr')} *</label><input type="text" value={taxForm.name_ar} onChange={(e) => setTaxForm({ ...taxForm, name_ar: e.target.value })} required style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 8 }} /></div>
                            <div style={{ marginTop: 15 }}><label style={{ display: 'block', marginBottom: 8, color: 'white' }}>{t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}</label><input type="text" value={taxForm.name_en} onChange={(e) => setTaxForm({ ...taxForm, name_en: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 8 }} /></div>
                            <div style={{ marginTop: 15 }}><label style={{ display: 'block', marginBottom: 8, color: 'white' }}>{t('quotations.add.modals.addTax.rate')} *</label><input type="number" value={taxForm.rate} onChange={(e) => setTaxForm({ ...taxForm, rate: e.target.value })} required style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 8 }} /></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 15 }}>
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={taxForm.is_active}
                                    onChange={(e) => setTaxForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                                    {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                                </label>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: 25 }}>
                                <button type="button" onClick={() => setShowAddTaxModal(false)} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer' }}>{t('common.cancel', { defaultValue: 'إلغاء' })}</button>
                                <button type="submit" style={{ backgroundColor: '#AC2000', padding: '10px 20px', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>{t('common.save')}</button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {showAddCostCenterModal && createPortal(
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowAddCostCenterModal(false)}>
                    <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '100%', border: '1px solid #333', direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h2 style={{ color: 'white' }}>{t('quotations.add.modals.addCostCenter.title')}</h2>
                            <button onClick={() => setShowAddCostCenterModal(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleAddCostCenter}>
                            <div><label style={{ display: 'block', marginBottom: 8, color: 'white' }}>{t('quotations.add.modals.addCostCenter.nameAr')} *</label><input type="text" value={costCenterForm.name_ar} onChange={(e) => setCostCenterForm({ ...costCenterForm, name_ar: e.target.value })} required style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 8 }} /></div>
                            <div style={{ marginTop: 15 }}><label style={{ display: 'block', marginBottom: 8, color: 'white' }}>{t('quotations.add.modals.addCostCenter.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}</label><input type="text" value={costCenterForm.name_en} onChange={(e) => setCostCenterForm({ ...costCenterForm, name_en: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 8 }} /></div>
                            <div style={{ marginTop: 15 }}><label style={{ display: 'block', marginBottom: 8, color: 'white' }}>{t('quotations.add.modals.addCostCenter.code')}</label><input type="text" value={costCenterForm.code} onChange={(e) => setCostCenterForm({ ...costCenterForm, code: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 8 }} /></div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: 25 }}>
                                <button type="button" onClick={() => setShowAddCostCenterModal(false)} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer' }}>{t('common.cancel', { defaultValue: 'إلغاء' })}</button>
                                <button type="submit" style={{ backgroundColor: '#AC2000', padding: '10px 20px', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>{t('common.save')}</button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default EditNoteReceivable;
